import type { TargetProbeLegMember, TargetProbeReachReason } from '@nadoutong/contracts';
import { memberReachable } from './legLocations.ts';

/**
 * Which routings actually hand a SKU's requests to its legs, and in what
 * proportion (#2658). A SKU is not one pool: the global default and every
 * customer config that covers it are separate weighted draws, and their
 * primary legs can differ.
 *
 * Built from the gateway's own reach classification on each member
 * (`reach_reason`, `listProbeLegTargets` in gateway `target-probes/store.ts`,
 * the §1 ladder of `docs/routing/behavior-tree.md`):
 *
 * - `override` (tier 0): the engaged emergency override. Always exclusive and
 *   not org-scoped (§1a), so while it covers the SKU it is the ONLY routing
 *   in effect; every other routing is dropped.
 * - `global` (tier 4): the assigned global config, one routing per config.
 * - `default_fallthrough` (tier 5): the SKU default targets, in effect for
 *   orgs without a covering config when no global config covers the SKU.
 * - `org_assigned` (tier 1): a config bound to >= 1 org, one routing per
 *   config. A member only exists where the config has a target for this SKU,
 *   i.e. where it covers the SKU and actually serves those orgs.
 * - `unassigned_config` / `shadowed_by_global_exclusive`: nothing routes
 *   there; ignored.
 *
 * Inherits the gateway's conservative gaps: org stack terminators (an
 * earlier exclusive config in one org's stack) are not modelled, and an
 * override engaged on a config that does not cover this SKU is invisible
 * here (no member says `override`).
 */

export type RoutingKind = 'override' | 'global' | 'default' | 'org' | 'unknown';

const KIND_OF: Readonly<Record<TargetProbeReachReason, RoutingKind | null>> = {
  override: 'override',
  global: 'global',
  default_fallthrough: 'default',
  org_assigned: 'org',
  unassigned_config: null,
  shadowed_by_global_exclusive: null,
};

const KIND_ORDER: readonly RoutingKind[] = ['override', 'global', 'default', 'org', 'unknown'];

export interface RoutingLegWeight {
  readonly legKey: string;
  /**
   * Sum of the ENABLED members' weights in this routing (a disabled target
   * is not a candidate at all). When every member is disabled, the
   * configured sum, so the leg still reads as the primary it was. Null when
   * a weight is unknown (gateway before #2619).
   */
  readonly weight: number | null;
  /** Every member of the leg in this routing is disabled by hand. */
  readonly disabled: boolean;
  /** Lowest `retry_order` among the leg's live members here; null sorts last (§4). */
  readonly retryOrder: number | null;
}

export interface EffectiveRouting {
  /** `override:<id>`, `global:<id>`, `default`, `org:<id>`, or `unknown`. */
  readonly key: string;
  readonly kind: RoutingKind;
  readonly configId: string | null;
  readonly configName: string | null;
  readonly assignedOrgs: number | null;
  readonly legs: readonly RoutingLegWeight[];
}

export interface RoutingLegInput {
  readonly key: string;
  readonly members: readonly TargetProbeLegMember[];
}

function knownWeight(member: TargetProbeLegMember): number | null {
  const weight: unknown = member.weight;
  return typeof weight === 'number' && Number.isFinite(weight) ? weight : null;
}

function memberKind(member: TargetProbeLegMember): RoutingKind | null {
  // A member from a gateway without reach fields cannot be placed; keep it
  // visible under one catch-all routing rather than dropping the leg.
  if (member.reach_reason === undefined) return 'unknown';
  if (!memberReachable(member)) return null;
  return KIND_OF[member.reach_reason];
}

function routingKey(kind: RoutingKind, member: TargetProbeLegMember): string {
  if (kind === 'default' || kind === 'unknown') return kind;
  return `${kind}:${member.config_id ?? '?'}`;
}

interface LegDraft {
  readonly enabledWeight: number | null;
  readonly allWeight: number | null;
  readonly enabled: boolean;
  readonly retryOrder: number | null;
  readonly allRetryOrder: number | null;
}

interface Draft {
  readonly routing: Omit<EffectiveRouting, 'legs'>;
  readonly legs: Map<string, LegDraft>;
}

function addWeight(sum: number | null, weight: number | null): number | null {
  return sum === null || weight === null ? null : sum + weight;
}

function minOrder(left: number | null, right: number | null): number | null {
  if (left === null) return right;
  if (right === null) return left;
  return Math.min(left, right);
}

function knownOrder(member: TargetProbeLegMember): number | null {
  const order: unknown = member.retry_order;
  return typeof order === 'number' && Number.isFinite(order) && order >= 1 ? order : null;
}

export function effectiveRoutings(legs: readonly RoutingLegInput[]): readonly EffectiveRouting[] {
  const drafts = new Map<string, Draft>();
  for (const leg of legs) {
    for (const member of leg.members) {
      const kind = memberKind(member);
      if (kind === null) continue;
      const key = routingKey(kind, member);
      const draft = drafts.get(key) ?? {
        routing: {
          key,
          kind,
          configId: kind === 'default' || kind === 'unknown' ? null : member.config_id ?? null,
          configName: member.config_name ?? null,
          assignedOrgs: member.config_assigned_orgs ?? null,
        },
        legs: new Map<string, LegDraft>(),
      };
      const weight = knownWeight(member);
      const order = knownOrder(member);
      const existing = draft.legs.get(leg.key) ?? {
        enabledWeight: 0,
        allWeight: 0,
        enabled: false,
        retryOrder: null,
        allRetryOrder: null,
      };
      draft.legs.set(leg.key, {
        enabledWeight: member.enabled ? addWeight(existing.enabledWeight, weight) : existing.enabledWeight,
        allWeight: addWeight(existing.allWeight, weight),
        enabled: existing.enabled || member.enabled,
        retryOrder: member.enabled ? minOrder(existing.retryOrder, order) : existing.retryOrder,
        allRetryOrder: minOrder(existing.allRetryOrder, order),
      });
      drafts.set(key, draft);
    }
  }
  const all = [...drafts.values()].map((draft): EffectiveRouting => ({
    ...draft.routing,
    legs: [...draft.legs.entries()].map(([legKey, entry]) => ({
      legKey,
      weight: entry.enabled ? entry.enabledWeight : entry.allWeight,
      disabled: !entry.enabled,
      retryOrder: entry.enabled ? entry.retryOrder : entry.allRetryOrder,
    })),
  }));
  const inEffect = all.some((routing) => routing.kind === 'override')
    ? all.filter((routing) => routing.kind === 'override')
    : all;
  return [...inEffect].sort((left, right) =>
    KIND_ORDER.indexOf(left.kind) - KIND_ORDER.indexOf(right.kind)
    || (right.assignedOrgs ?? 0) - (left.assignedOrgs ?? 0)
    || (left.configName ?? '').localeCompare(right.configName ?? ''));
}

export type LegRole = 'primary' | 'fallback' | 'unknown';

export interface RoutingShare {
  readonly legKey: string;
  readonly weight: number | null;
  readonly role: LegRole;
  /** Whole-percent share of first picks inside this routing; null unless `primary`. Sums to 100. */
  readonly percent: number | null;
  readonly disabled: boolean;
}

function roleOf(weight: number | null): LegRole {
  if (weight === null) return 'unknown';
  return weight > 0 ? 'primary' : 'fallback';
}

/**
 * Shares inside one routing, by configured weight over the legs that are
 * candidates at all: a disabled leg is not in the draw (the tenant routing
 * pool bar excludes it the same way). A tripped leg keeps its slice so the
 * bar can stripe what it gave up. Largest-remainder rounding.
 */
export function routingShares(routing: EffectiveRouting): readonly RoutingShare[] {
  const counted = (leg: RoutingLegWeight): number => (leg.disabled ? 0 : Math.max(0, leg.weight ?? 0));
  const total = routing.legs.reduce((sum, leg) => sum + counted(leg), 0);
  const exact = routing.legs.map((leg) => (total > 0 ? (counted(leg) * 100) / total : 0));
  const floors = exact.map((value) => Math.floor(value));
  let leftover = total > 0 ? 100 - floors.reduce((sum, value) => sum + value, 0) : 0;
  const order = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .filter((entry) => (exact[entry.index] ?? 0) > 0)
    .sort((left, right) => right.remainder - left.remainder || left.index - right.index);
  const percents = [...floors];
  for (const entry of order) {
    if (leftover <= 0) break;
    percents[entry.index] = (percents[entry.index] ?? 0) + 1;
    leftover -= 1;
  }
  return routing.legs.map((leg, index) => {
    const role = roleOf(leg.weight);
    return {
      legKey: leg.legKey,
      weight: leg.weight,
      role,
      percent: role === 'primary' && !leg.disabled && total > 0 ? percents[index] ?? 0 : null,
      disabled: leg.disabled,
    };
  });
}

/**
 * Where a weight-0 leg takes the first pick (behavior-tree §3a/§3b/§4b):
 * in a region where every weight > 0 leg of the routing is disabled or
 * retired by an armed trip, the draw has no positive weight, so the chain
 * head — the first live leg by `retry_order` — gets every first pick.
 * Not a takeover: a routing that never had a weight > 0 leg (always chain
 * order), or one where every enabled leg is tripped (the draw then uses the
 * original weights). §3b ranks `retry_order → priority → jitter`, but
 * `priority` is a constant literal inside every routing tier (behavior-tree
 * "Per-tier ranking inputs": 1000 on config tiers, 100 on SKU defaults) and
 * a routing here is one tier, so it never breaks a tie: ties on
 * `retry_order` fall to per-request jitter, and every tied leg is returned.
 *
 * Returns legKey → regions where it took over.
 */
export function routingTakeovers(
  routing: EffectiveRouting,
  regions: readonly string[],
  retiredIn: (legKey: string, region: string) => boolean,
): ReadonlyMap<string, readonly string[]> {
  const out = new Map<string, string[]>();
  if (!routing.legs.some((leg) => (leg.weight ?? 0) > 0)) return out;
  for (const region of regions) {
    const live = routing.legs.filter((leg) => !leg.disabled && !retiredIn(leg.legKey, region));
    if (live.length === 0) continue;
    if (live.some((leg) => (leg.weight ?? 0) > 0 || leg.weight === null)) continue;
    const orderOf = (leg: RoutingLegWeight): number => leg.retryOrder ?? Number.POSITIVE_INFINITY;
    const head = Math.min(...live.map(orderOf));
    for (const leg of live.filter((entry) => orderOf(entry) === head)) {
      out.set(leg.legKey, [...(out.get(leg.legKey) ?? []), region]);
    }
  }
  return out;
}

export interface LegRoutingRole {
  readonly routing: EffectiveRouting;
  readonly share: RoutingShare;
  /** Regions where this (weight 0) leg now takes every first pick of the routing. */
  readonly takeover: readonly string[];
}

/** Per routing key: legKey → regions it took over (see {@link routingTakeovers}). */
export type RoutingTakeovers = ReadonlyMap<string, ReadonlyMap<string, readonly string[]>>;

/** The leg's role in every effective routing it appears in, in routing order. */
export function legRoutingRoles(
  legKey: string,
  routings: readonly EffectiveRouting[],
  takeovers: RoutingTakeovers = new Map(),
): readonly LegRoutingRole[] {
  const out: LegRoutingRole[] = [];
  for (const routing of routings) {
    const share = routingShares(routing).find((entry) => entry.legKey === legKey);
    if (share !== undefined) {
      out.push({ routing, share, takeover: takeovers.get(routing.key)?.get(legKey) ?? [] });
    }
  }
  return out;
}

/**
 * - `first` 首发腿: weight > 0 (or unknown) in ANY routing in effect where
 *   it is not disabled — by configuration, so a tripped primary stays here.
 * - `reserve` 兜底腿: weight 0 wherever it is enabled.
 * - `manual` 已手动摘除: disabled in every routing it appears in.
 * - `overridden`: reachable, but the engaged emergency override took every
 *   routing it sits in out of effect; still probed on schedule.
 * - `dead` 不接流量: no reachable member.
 */
export type LegSection = 'first' | 'reserve' | 'manual' | 'overridden' | 'dead';

export function legSection(reachable: boolean, roles: readonly LegRoutingRole[]): LegSection {
  if (!reachable) return 'dead';
  if (roles.length === 0) return 'overridden';
  const live = roles.filter((role) => !role.share.disabled);
  if (live.length === 0) return 'manual';
  return live.some((role) => role.share.role !== 'fallback') ? 'first' : 'reserve';
}

/** Sections whose legs take traffic: the only ones counted as live. */
export function sectionIsLive(section: LegSection): boolean {
  return section === 'first' || section === 'reserve';
}
